/**
 * ScopeBar — the one place that picks the product and channel (Ads) or network (Social). It sits under the top bar on
 * every Ads and Social page; pages never show their own product or channel controls. The choice is written to the
 * address and remembered on this device (see lib/scope.ts).
 */
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { AD_CHANNEL_LABEL, AD_CHANNELS, AD_PRODUCTS, AD_PRODUCT_IDS, SOCIAL_NETWORK_CHOICES } from "@demandhub-cms/shared";
import { isSocialPath, useScope } from "../lib/scope";
import { SegmentedControl } from "@/components/ui/segmented-control";

export function ScopeBar() {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const scope = useScope();
  const social = isSocialPath(pathname);

  // A page opened without a scope in the address gets one written in, so the link you copy keeps it.
  useEffect(() => {
    const missing = !params.get("product") || !params.get(social ? "network" : "channel");
    if (!missing) return;
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set("product", scope.product);
        n.set(social ? "network" : "channel", social ? scope.network : scope.channel);
        return n;
      },
      { replace: true }
    );
  }, [params, social, scope.product, scope.channel, scope.network, setParams]);

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-neutral-200 bg-white px-4 py-2 sm:px-6" role="group" aria-label="Scope">
      <div className="flex items-center gap-2">
        <span id="scope-product-l" className="text-xs font-medium text-neutral-600">Product</span>
        <SegmentedControl labelledBy="scope-product-l" value={scope.product} onChange={scope.setProduct} items={AD_PRODUCT_IDS.map((id) => ({ id, label: AD_PRODUCTS[id].name }))} />
      </div>
      {social ? (
        <div className="flex items-center gap-2">
          <span id="scope-network-l" className="text-xs font-medium text-neutral-600">Network</span>
          <SegmentedControl
            labelledBy="scope-network-l"
            value={scope.network}
            onChange={scope.setNetwork}
            items={[{ id: "all", label: "All" }, ...SOCIAL_NETWORK_CHOICES.filter((n) => ["linkedin", "x", "facebook", "instagram"].includes(n.value)).map((n) => ({ id: n.value, label: n.label }))]}
          />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span id="scope-channel-l" className="text-xs font-medium text-neutral-600">Channel</span>
          <SegmentedControl labelledBy="scope-channel-l" value={scope.channel} onChange={scope.setChannel} items={AD_CHANNELS.map((id) => ({ id, label: AD_CHANNEL_LABEL[id] }))} />
        </div>
      )}
    </div>
  );
}
